/**
 * deviceType - Centralized participant data-source detection.
 *
 * A participant may contribute data from one or more sources (Fitbit, HealthKit,
 * and potentially more in future). Fresh Fitbit connections use the server's
 * safe `healthConnection` projection; legacy records fall back to token /
 * authorization presence. The stored `dataSource` field is intentionally NOT
 * consulted because it can go stale and hide a participant's real data.
 *
 * TWO public surfaces:
 *  - SOURCE LIST (preferred for DISPLAY): `getDeviceSources` / `formatDeviceSources`
 *    / `participantHasSource`. Renders "Fitbit, HealthKit" and is EXTENSIBLE — to
 *    add a source, add a detector + ONE `SOURCE_REGISTRY` entry; labels, filtering
 *    and display all derive from it.
 *  - ENUM (kept for LOGIC/API): `getDeviceType` / `deviceTypeMatchesSource` return
 *    the 'fitbit'|'healthkit'|'both'|'none' value the backend metric-coverage API
 *    and the study-level metric selectors consume. Derived from the source list.
 */

/** A single data source a participant can contribute from. Extend here. */
export type DataSource = 'fitbit' | 'healthkit' | 'ultrahuman';

/** Enum classification consumed by the backend API + study-level selectors. */
export type DeviceType = 'fitbit' | 'healthkit' | 'both' | 'none';

/**
 * One device of a participant and its state, as decided by the server for every screen
 * (newbackend/src/config/participantDevices.js, docs/specs/participant-devices.md).
 */
export type DeviceStatus = 'invited' | 'connected' | 'needs_reconnect' | 'disconnected';
export interface ParticipantDevice { id: DataSource; status: DeviceStatus }

/**
 * Minimal structural shape needed for device-type detection. Kept loose on
 * purpose so it accepts both the canonical `Participant` type and the local
 * extended shapes used by individual dashboard components.
 */
export interface DeviceTypeParticipant {
  devices?: ParticipantDevice[] | null;
  dataSource?: 'fitbit' | 'healthkit' | 'both';
  fitbitCollectionEnrollment?: boolean;
  ultrahumanCollectionEnrollment?: boolean;
  ultrahumanEnrollmentStatus?: string;
  healthConnection?: {
    provider?: 'fitbit_legacy' | 'google_health';
    status?: 'active' | 'action_required' | 'disabled';
  } | null;
  fitbitTokens?: { accessToken?: string } | null;
  fitbitData?: { accessToken?: string } | null;
  fitbitAccessToken?: string | null;
  fitbitUserId?: string | null;
  fitbitConnected?: boolean | null;
  hasValidFitbit?: boolean | null;
  hasTokens?: boolean | null;
  healthkit?: { authorized?: boolean } | null;
  healthkitConnected?: boolean | null;
  hasValidHealthKit?: boolean | null;
}

export function isFreshFitbitCollectionParticipant(
  participant: DeviceTypeParticipant
): boolean {
  return participant.fitbitCollectionEnrollment === true;
}

export function participantHasFitbit(
  participant: DeviceTypeParticipant
): boolean {
  // A person Fitbit joined keeps Fitbit's state in its own section, so the server's device
  // list is the only place that says their Fitbit enrollment is complete.
  if (Array.isArray(participant.devices) &&
    participant.devices.some(device => device.id === 'fitbit' && device.status !== 'invited')) {
    return true;
  }
  // The safe Google Health summary is authoritative for the fresh Fitbit
  // product connection, even when retired token-shaped fields remain.
  if (participant.healthConnection?.provider === 'google_health') {
    return participant.healthConnection.status === 'active';
  }

  return !!(
    participant.fitbitTokens?.accessToken ||
    participant.fitbitData?.accessToken ||
    participant.fitbitAccessToken ||
    participant.fitbitUserId ||
    participant.fitbitConnected ||
    participant.hasValidFitbit ||
    participant.hasTokens
  );
}

export function participantHasHealthKit(
  participant: DeviceTypeParticipant
): boolean {
  return !!(
    participant.healthkit?.authorized ||
    participant.healthkitConnected ||
    participant.hasValidHealthKit
  );
}

/**
 * The registry of data sources. THIS is the only place to edit when adding a new
 * source: append `{ id, label, has }`. Order here drives display/iteration order.
 */
const SOURCE_REGISTRY: ReadonlyArray<{
  id: DataSource;
  label: string;
  has: (p: DeviceTypeParticipant) => boolean;
}> = [
  { id: 'fitbit', label: 'Fitbit', has: participantHasFitbit },
  { id: 'healthkit', label: 'HealthKit', has: participantHasHealthKit },
  { id: 'ultrahuman', label: 'Ultrahuman', has: participant => participant.ultrahumanCollectionEnrollment === true && participant.ultrahumanEnrollmentStatus === 'completed' },
];

/** All known data source ids, in registry order. */
export const DATA_SOURCES: ReadonlyArray<DataSource> = SOURCE_REGISTRY.map(
  (s) => s.id
);

/**
 * The server's per-device view, or null from an older server that does not send it (then
 * every function below keeps its earlier behavior).
 */
export function participantDevices(participant: DeviceTypeParticipant): ParticipantDevice[] | null {
  return Array.isArray(participant.devices) ? participant.devices : null;
}

/**
 * Sources a participant has contributed data from (token/auth inference). Used to choose
 * whose stored data to include, so an older Fitbit participant with expired tokens still
 * counts. Status, attention and history panels use the server's devices instead.
 */
export function getDeviceSources(
  participant: DeviceTypeParticipant
): DataSource[] {
  return SOURCE_REGISTRY.filter((s) => s.has(participant)).map((s) => s.id);
}

/** Active when at least one device is connected; null when the server sent no devices. */
export function participantIsActive(participant: DeviceTypeParticipant): boolean | null {
  const devices = participantDevices(participant);
  return devices && devices.length ? devices.some(device => device.status === 'connected') : null;
}

/**
 * What needs attention across this person's devices, e.g. "Fitbit reconnection required" or
 * "Fitbit not connected yet"; '' when nothing does; null when the server sent no devices.
 */
export function deviceAttention(participant: DeviceTypeParticipant): string | null {
  const devices = participantDevices(participant);
  if (!devices || !devices.length) return null;
  const anyConnected = devices.some(device => device.status === 'connected');
  return devices.flatMap(device => {
    const label = sourceLabel(device.id);
    if (device.status === 'needs_reconnect') return [`${label} reconnection required`];
    if (device.status === 'disconnected') return [`${label} connection disabled`];
    if (device.status === 'invited') return [anyConnected ? `${label} not connected yet` : 'Enrollment not complete'];
    return [];
  }).filter((reason, index, all) => all.indexOf(reason) === index).join(' · ');
}

/** Collection-history panels to show: devices past the invitation step. */
export function historySources(participant: DeviceTypeParticipant): Array<'fitbit' | 'ultrahuman'> {
  const devices = participantDevices(participant);
  const fitbit = participant.fitbitCollectionEnrollment === true;
  if (!devices) {
    return [...(fitbit ? ['fitbit' as const] : []),
      ...(participant.ultrahumanCollectionEnrollment && participant.ultrahumanEnrollmentStatus === 'completed' ? ['ultrahuman' as const] : [])];
  }
  return (['fitbit', 'ultrahuman'] as const).filter(id => (id !== 'fitbit' || fitbit) &&
    devices.some(device => device.id === id && device.status !== 'invited'));
}

/** Devices invited but not connected yet, shown as a note instead of a history panel. */
export function invitedSources(participant: DeviceTypeParticipant): DataSource[] {
  return (participantDevices(participant) || []).filter(device => device.status === 'invited').map(device => device.id);
}

/** Human label for a source id ('fitbit' -> 'Fitbit'). */
export function sourceLabel(source: DataSource): string {
  return SOURCE_REGISTRY.find((s) => s.id === source)?.label ?? source;
}

/** Display string: 'Fitbit, HealthKit' | 'Fitbit' | 'None'. Replaces "Both". */
export function formatDeviceSources(participant: DeviceTypeParticipant): string {
  const labels = getDeviceSources(participant).map(sourceLabel);
  return labels.length ? labels.join(', ') : 'None';
}

/** True when the participant contributes from `source`. */
export function participantHasSource(
  participant: DeviceTypeParticipant,
  source: DataSource
): boolean {
  return SOURCE_REGISTRY.some((s) => s.id === source && s.has(participant));
}

/**
 * Enum classification for LOGIC/API (backend metric-coverage + study-level
 * selectors). Derived from the source list: more than one source -> 'both'. The
 * stored `dataSource` is NOT consulted (see header).
 */
export function getDeviceType(participant: DeviceTypeParticipant): DeviceType {
  const sources = getDeviceSources(participant).filter(source => source === 'fitbit' || source === 'healthkit');
  if (sources.length > 1) return 'both';
  return (sources[0] as DeviceType) ?? 'none';
}

/**
 * True when a participant with the given device type should be included for a
 * single-source selection. A 'both' participant matches BOTH 'fitbit' and
 * 'healthkit' selections; an exact match always counts.
 */
export function deviceTypeMatchesSource(
  deviceType: DeviceType,
  selected: 'fitbit' | 'healthkit'
): boolean {
  return deviceType === selected || deviceType === 'both';
}
